import { History, Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { cn } from '@apps/web/lib/utils';
import {
    computeLineDiffPreview,
    type LineDiffRow,
} from '@apps/web/routes/_app/admin/agent/line_diff';
import {
    revertAdminAgentSkillVersion,
    useAdminAgentSkillHistory,
    useAdminAgentSkillVersion,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type components } from '@libraries/typescript/api_client';

type SkillVersion = components['schemas']['AdminAgentSkillOverrideRead'];

export function SkillOverrideChangeCard({
    versionId,
}: {
    readonly versionId: string;
}) {
    const {
        data: version,
        error: versionError,
        isLoading: versionLoading,
    } = useAdminAgentSkillVersion(versionId);
    const { data: history, mutate: mutateHistory } = useAdminAgentSkillHistory(
        version?.skill_name ?? null,
    );
    const [isReverting, setIsReverting] = useState(false);
    const [revertedVersion, setRevertedVersion] = useState<SkillVersion | null>(
        null,
    );
    const [revertError, setRevertError] = useState<string | null>(null);

    async function revert() {
        if (!version || isReverting) {
            return;
        }
        setIsReverting(true);
        setRevertError(null);
        try {
            const reverted = await revertAdminAgentSkillVersion(version.id);
            setRevertedVersion(reverted);
            await mutateHistory();
        } catch {
            setRevertError(
                'Unable to revert this version. The active skill was not changed.',
            );
        } finally {
            setIsReverting(false);
        }
    }

    if (versionLoading) {
        return <LoadingChangeCard />;
    }
    if (versionError || !version) {
        return (
            <Card>
                <CardContent className="py-3 text-xs text-[var(--aa-ink-muted)]">
                    The applied skill version could not be loaded.
                </CardContent>
            </Card>
        );
    }

    const diff = computeLineDiffPreview(version.previous_body, version.body);

    return (
        <Card className="border-[var(--aa-purple-border)] bg-white shadow-sm">
            <CardHeader className="border-b border-[var(--aa-border)] pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <CardTitle className="font-mono text-sm text-[var(--aa-ink)]">
                            Skill override applied
                        </CardTitle>
                        <CardDescription className="text-xs">
                            {version.skill_name} · version {version.version}
                        </CardDescription>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={isReverting || revertedVersion !== null}
                        onClick={() => void revert()}
                    >
                        {isReverting ? (
                            <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                            <RotateCcw className="size-3.5" />
                        )}
                        Revert
                    </Button>
                </div>
                <p className="text-xs text-[var(--aa-ink-muted)]">
                    This version is active immediately. The diff below is
                    computed from the version records returned by the API.
                </p>
                {revertedVersion ? (
                    <p className="text-xs text-[var(--aa-purple-strong)]">
                        Reverted as new active version {revertedVersion.version}
                        .
                    </p>
                ) : null}
                {revertError ? (
                    <p className="text-destructive text-xs">{revertError}</p>
                ) : null}
            </CardHeader>
            <CardContent className="pt-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-xs font-semibold text-[var(--aa-ink)]">
                        Raw before / after diff
                    </h3>
                    <Badge variant="secondary">Active</Badge>
                </div>
                {diff.kind === 'raw' ? (
                    <div className="grid gap-3 lg:grid-cols-2">
                        <RawBodyPanel
                            title={
                                version.previous_version === null
                                    ? 'Baked skill body'
                                    : `Version ${version.previous_version}`
                            }
                            body={version.previous_body}
                        />
                        <RawBodyPanel
                            title={`Version ${version.version}`}
                            body={version.body}
                        />
                    </div>
                ) : (
                    <InlineDiff rows={diff.rows} />
                )}
                <SkillVersionHistory history={history ?? []} />
            </CardContent>
        </Card>
    );
}

function LoadingChangeCard() {
    return (
        <Card>
            <CardContent className="flex items-center gap-2 py-3 text-xs text-[var(--aa-ink-muted)]">
                <Loader2 className="size-3.5 animate-spin" />
                Loading the applied skill version…
            </CardContent>
        </Card>
    );
}

function SkillVersionHistory({
    history,
}: {
    readonly history: ReadonlyArray<SkillVersion>;
}) {
    if (history.length === 0) {
        return null;
    }
    return (
        <details className="mt-3 rounded-md border border-[var(--aa-border)] px-3 py-2">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-medium text-[var(--aa-ink)]">
                <History className="size-3.5" />
                Version history ({history.length})
            </summary>
            <div className="mt-2 flex flex-col gap-1.5">
                {history.map((item) => (
                    <div
                        className="flex items-baseline justify-between gap-2 text-xs text-[var(--aa-ink-muted)]"
                        key={item.id}
                    >
                        <span className="font-mono">v{item.version}</span>
                        <span className="truncate">{item.rationale}</span>
                    </div>
                ))}
            </div>
        </details>
    );
}

function InlineDiff({ rows }: { readonly rows: Array<LineDiffRow> }) {
    return (
        <pre className="max-h-[min(40vh,420px)] overflow-auto rounded-md border border-[var(--aa-border)] bg-[var(--aa-surface-sunk)] p-3 font-mono text-[11px] leading-5">
            {rows.map((line, index) => (
                <code
                    className={cn(
                        'block min-h-5 whitespace-pre-wrap',
                        line.op === 'add' &&
                            'bg-green-500/15 text-green-700 dark:text-green-300',
                        line.op === 'remove' &&
                            'bg-red-500/15 text-red-700 dark:text-red-300',
                        line.op === 'context' && 'text-[var(--aa-ink-muted)]',
                    )}
                    key={`${line.op}-${index}`}
                >
                    {line.op === 'add'
                        ? '+ '
                        : line.op === 'remove'
                          ? '- '
                          : '  '}
                    {line.text}
                </code>
            ))}
        </pre>
    );
}

function RawBodyPanel({
    title,
    body,
}: {
    readonly title: string;
    readonly body: string;
}) {
    return (
        <div className="min-w-0">
            <h4 className="mb-1 text-[11px] font-semibold text-[var(--aa-ink)]">
                {title}
            </h4>
            <pre className="max-h-[min(40vh,420px)] overflow-auto rounded-md border border-[var(--aa-border)] bg-[var(--aa-surface-sunk)] p-3 font-mono text-[11px] leading-5 break-words whitespace-pre-wrap text-[var(--aa-ink-muted)]">
                {body}
            </pre>
        </div>
    );
}
